import { getBankStatementItemsTable, removeBankStatementItem } from "@/requests/finances/requestsBankOperations";

/*eslint  @typescript-eslint/no-use-before-define: 0*/
import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { FILTER_NAMES } from "@/static/filterNames";
import { DataOptions } from "vuetify";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { GetBankStatementItemsItem } from "@/models/api/finances/GetBankStatementItemsResponse";
import { BANK_STATEMENT_TYPES } from "@/static/bankStatementTypes";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import requiredValidator from "@/rules/requiredValidator";
import { PromptModel, PROMPT_TYPE } from "@/models/components/popup/Prompt";
import { getIsLockedBankStatementItemEdit } from "@/requests/finances/requestsBankTransfers";
import { MESSAGE_TYPES } from "@/static/messageTypes";
import { AlertModelInterface } from "@/models/components/popup/Alert";

/* eslint-disable-next-line */
export interface bankOperationsPurchaseLeftPanelStateInterface {
  isLoading: boolean;
  dataTableData: Array<GetBankStatementItemsItem>;
  dataTableOptions: DataOptions;
  countServerSide: number;
  filters: null | Array<CriterionItemRequest>;
  selectedItemId: null | number;
}

export const bankOperationsPurchaseLeftPanelState: bankOperationsPurchaseLeftPanelStateInterface = {
  isLoading: false,
  dataTableData: [],
  dataTableOptions: new DefaultDataOptions(),
  countServerSide: -1,
  filters: null,
  selectedItemId: null,
};

const bankOperationsPurchaseLeftPanelModule = defineModule({
  state: bankOperationsPurchaseLeftPanelState as bankOperationsPurchaseLeftPanelStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setDataTableData(state, param: Array<GetBankStatementItemsItem>) {
      state.dataTableData = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setCountServerSide(state, param: number) {
      state.countServerSide = param;
    },
    setFilters(state, param: Array<CriterionItemRequest>) {
      state.filters = param;
    },
    setSelectedItemId(state, param: null | number) {
      state.selectedItemId = param;
    },
  },
  actions: {
    async getDataTableData(context) {
      const { commit, state, rootDispatch } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getBankStatementItemsTable(
        {
          count: state.dataTableOptions.itemsPerPage,
          page: state.dataTableOptions.page - 1,
          criterions: state.filters,
          filterName: FILTER_NAMES.BankStatementItemStandardFilter,
        },
        BANK_STATEMENT_TYPES.Purchase
      );
      commit.setCountServerSide(response.totalCount);
      commit.setDataTableData(response.items);
      commit.setIsLoading(false);
      if (response.items.length > 0) {
        commit.setSelectedItemId(response.items[0].id);
        rootDispatch.finances.bankOperations.purchaseRightPanel.getDataTableData(response.items[0].id);
      } else {
        commit.setSelectedItemId(null);
      }
    },
    async dropPayment(context) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.selectedItemId !== null) {
        rootDispatch.failIfRequestsInProgress();

        const removeBSItem = await dispatch.canUserRemoveBankStatementItem([state.selectedItemId]);
        if (removeBSItem) {
          const promptContent = filterCapitalize(i18n.t("addCommentAboutReasonOfDroppingPayment")) + ":";
          const promptTitle = filterCapitalize(i18n.t("paymentDropping"));
          const promptModel = new PromptModel(promptContent, promptTitle, PROMPT_TYPE.TEXTAREA);
          promptModel.width = "500px";
          promptModel.rules = [requiredValidator];
          promptModel.placeholder = filterCapitalize(i18n.t("comment"));
          const promptResponse = (await rootDispatch.popup.addPrompt(promptModel)) as string;
          await removeBankStatementItem(state.selectedItemId, promptResponse);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          dispatch.getDataTableData();
        }
      }
    },
    async canUserRemoveBankStatementItem(context, bankStatementIds: Array<number>) {
      const { rootDispatch } = _moduleActionContext(context);
      const isLockedEditResponse = await getIsLockedBankStatementItemEdit(bankStatementIds);
      if (!isLockedEditResponse.isLockedEdit) {
        if (await rootDispatch.popup.addConfirmRemoveElement()) return true;
        else return false;
      }
      if (isLockedEditResponse.isLockedEdit && isLockedEditResponse.userHasPermission) {
        if (
          await rootDispatch.popup.addConfirmRemoveElementWithCustomMessage(
            i18n.t("editThisItemIsLockedButYouHavePermissionToChange") +
              " " +
              filterCapitalize(i18n.t("doYouReallyWantToRemoveElement?"))
          )
        )
          return true;
        else return false;
      }
      rootDispatch.popup.addAlert({
        title: filterCapitalize(i18n.t("error")),
        content: filterCapitalize(i18n.t("noPermissions")),
        type: MESSAGE_TYPES.Error,
      } as AlertModelInterface);
      return false;
    },
  },
});

export default bankOperationsPurchaseLeftPanelModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, bankOperationsPurchaseLeftPanelModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, bankOperationsPurchaseLeftPanelModule);
